import type { StreamHudData } from './stream-hud'
import type { AppLocale } from '@/i18n/locales'
import { enabledBuffCount, type BuffSnapshot } from '@/buffs/types'
import type { DailyPanelSkin } from './pip-skins'
import { streamLayoutContent } from './stream-hud'
import { formatRate } from '@/core/display-settings'
import { performanceDiagnostics } from '@/services/performance-diagnostics'

const digitFootprint = (value: string) => value.replace(/[0-9]/g, '0')

/** Tabular digits share a footprint; chart samples and evidence clocks do not affect layout. */
export function dailyPanelLayoutContent({ data, locale, theme, buffs }: DailyPanelProps): string {
  const stable = { ...data }
  for (const key of ['level', 'expRate', 'expPercent', 'eta', 'expGained', 'fullDuration',
    'mesosRate', 'mesosNet', 'mesosStart', 'mesosBalanceChange', 'hpPercent', 'mpPercent'] as const) {
    stable[key] = digitFootprint(data[key])
  }
  const recent = data.expChart?.recent
  const live = recent?.status === 'ready' && recent.value != null && Number.isFinite(recent.value)
    ? formatRate(recent.value, locale, data.rateUnit, { maximumFractionDigits: 1 }) : '--'
  const chartHasPoints = data.expChart?.points.some(point => point.value !== null && Number.isFinite(point.value)
    && point.at >= data.expChart!.from && point.at <= data.expChart!.to) ?? false
  return JSON.stringify([streamLayoutContent(stable), stable.mesosNet, locale, theme, data.status, data.statusText,
    data.expReadout?.showLabel ? data.expReadout.label : null,
    digitFootprint(live), digitFootprint(data.expRateTrend?.text ?? ''), chartHasPoints,
    chartHasPoints ? null : [recent?.status, digitFootprint(String(Math.floor((recent?.continuousMs ?? 0) / 1000))), recent?.windowMs],
    buffs?.items.length ?? 0, enabledBuffCount(buffs)])
}

const fittedPanels = new WeakMap<HTMLElement, string>()

export interface DailyPanelProps {
  data: StreamHudData
  locale: AppLocale
  theme: 'light' | 'dark'
  buffs?: BuffSnapshot | null
  preview?: boolean
}

export function dailyPanelBackground(skin: DailyPanelSkin, theme: 'light' | 'dark'): string {
  const colors = { 'daily-order': ['#ffffff', '#1b1d1f'] } as const
  return colors[skin][theme === 'dark' ? 1 : 0]
}

export function dailyPanelFits(root: HTMLElement): boolean {
  const bounds = root.getBoundingClientRect()
  if (!bounds.width || !bounds.height) return true
  if (root.scrollHeight > root.clientHeight + 1 || root.scrollWidth > root.clientWidth + 1) return false
  const main = root.querySelector<HTMLElement>('.daily-main')
  if (main && (main.scrollHeight > main.clientHeight + 1 || main.scrollWidth > main.clientWidth + 1)) return false
  const range = root.ownerDocument.createRange()
  for (const node of root.querySelectorAll<HTMLElement>('[data-panel-measure], .daily-chart-empty-heading, .daily-chart-empty-status, .daily-readouts .daily-label, .daily-value, .daily-average, .exp-live, .daily-controls button, .daily-chart')) {
    if (!node.getClientRects().length) continue
    const box = node.getBoundingClientRect()
    if (box.left < bounds.left - 1 || box.right > bounds.right + 1 || box.top < bounds.top - 1 || box.bottom > bounds.bottom + 1) return false
    if (node.scrollWidth > node.clientWidth + 1) return false
    for (const child of node.childNodes) {
      if (child.nodeType !== 3 || !child.textContent?.trim()) continue
      range.selectNodeContents(child)
      const text = range.getBoundingClientRect()
      if (text.left < box.left - 1 || text.right > box.right + 1 || text.bottom > bounds.bottom + 1) return false
    }
  }
  return true
}

/** Long command labels should compact the toolbar before retiring metric sections. */
function dailyControlsFit(root: HTMLElement): boolean {
  const footer = root.querySelector<HTMLElement>('.daily-footer')
  if (!footer?.getClientRects().length) return true
  const bounds = footer.getBoundingClientRect()
  for (const node of [footer, ...footer.querySelectorAll<HTMLElement>('.daily-controls, .daily-controls button, [data-widget-elapsed]')]) {
    if (!node.getClientRects().length) continue
    const box = node.getBoundingClientRect()
    if (box.left < bounds.left - 1 || box.right > bounds.right + 1 || node.scrollWidth > node.clientWidth + 1) return false
  }
  return true
}

/** Actual content decides detail. Average and live are never part of a hide tier. */
export function fitDailyPanel(root: HTMLElement, contentKey?: string): void {
  const { clientWidth: width, clientHeight: height } = root
  const key = JSON.stringify([width, height, contentKey])
  if (contentKey !== undefined && fittedPanels.get(root) === key) return
  const started = performance.now()
  try {
    fitDailyPanelContent(root, width, height)
    if (contentKey !== undefined && width && height) fittedPanels.set(root, key)
  } finally { performanceDiagnostics.measure('widget-layout', performance.now() - started) }
}

function fitDailyPanelContent(root: HTMLElement, width: number, height: number): void {
  root.dataset.shape = width >= 480 && height <= 220 ? 'strip' : width < 280 ? 'portrait' : 'classic'
  root.dataset.small = String(width < 280 || height <= 200)
  root.dataset.micro = String(width < 190 || height < 120)
  root.dataset.iconActions = String(width < 280 || height < 120)
  root.dataset.stacked = 'false'
  root.dataset.hideTrend = 'false'
  delete root.dataset.tight
  const highest = height < 120 ? 0 : height <= 180 && width < 480 ? 2 : 8
  for (let detail = highest; detail >= 0; detail--) {
    root.dataset.detail = String(detail)
    if (root.dataset.iconActions === 'false' && !dailyControlsFit(root)) root.dataset.iconActions = 'true'
    const readouts = root.querySelector<HTMLElement>('.daily-readouts')
    const average = readouts?.querySelector<HTMLElement>('.daily-average .daily-value')
    // Retire the optional comparison before it can stack either core readout.
    // Its permanent footprint is measured even while its contents are invisible.
    if (average && average.scrollWidth > average.clientWidth + 1) root.dataset.hideTrend = 'true'
    if (readouts && [...readouts.querySelectorAll<HTMLElement>('.daily-value')].some(node => node.scrollWidth > node.clientWidth + 1)) root.dataset.stacked = 'true'
    root.dataset.hideChartSamples = 'false'
    const emptyStatus = root.querySelector<HTMLElement>('.daily-chart-empty-status')
    if (emptyStatus && emptyStatus.scrollWidth > emptyStatus.clientWidth + 1) root.dataset.hideChartSamples = 'true'
    if (dailyPanelFits(root)) return
  }
  root.dataset.tight = 'true'
}
